"use client";

/**
 * Records a signed-in user's VIEW of a saved /c comparison page into My
 * searches (2026-08-11), so Browse / home-chip / direct-link visits are
 * captured too - not just typed searches through /compare.
 *
 * Renders nothing. On mount, once, if the user is signed in, it POSTs the slug
 * (with the Clerk Bearer token, mirroring the watches/searches clients) to
 * /api/searches/record-view, which resolves the slug's cached comparison and
 * upserts one My searches row per product. Fire-and-forget + best-effort: it
 * never blocks render and any failure is swallowed. Anonymous visitors do
 * nothing (they have no My searches page).
 */

import { useEffect, useRef } from "react";

import { useAuth, useUser } from "@clerk/nextjs";

import { trackComparisonViewed } from "@/lib/analytics";
import { recordComparisonView } from "@/lib/api/searches";
import { takeSearchHandoff } from "@/lib/searchHandoff";

export function RecordComparisonView({ slug }: { slug: string }) {
  const { isSignedIn } = useUser();
  const { getToken } = useAuth();
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current || !slug) return;
    fired.current = true;
    // B-290 item 2 (2026-09-28): a finished search switching to the page it
    // just saved is the same look, already recorded by the search (its ledger
    // row, search_completed, My searches). Recording it again would count
    // every search twice and, signed in, feed the club robot's paid queue,
    // which only a real page open may feed (owner rule, 23 Sep).
    if (takeSearchHandoff(slug, "record_view")) return;
    // B-169: EVERY view reaches the shopper action ledger, signed in or
    // not. The beacon carries only the slug; the backend resolves which
    // offers were on the page. A signed-in view is recorded by the
    // authenticated call below instead, so the beacon says so and the
    // backend skips it (one view, one row).
    trackComparisonViewed({ productSlug: slug, signedIn: Boolean(isSignedIn) });
    if (!isSignedIn) return;
    (async () => {
      try {
        const token = (await getToken()) ?? "";
        if (token) await recordComparisonView(slug, token);
      } catch {
        /* best-effort */
      }
    })();
  }, [isSignedIn, slug, getToken]);

  return null;
}
